import cx from "classnames";
import React, { JSX } from "react";
import { ConfidenceTierBadge } from "@app/shared/ConfidenceTierBadge";
import { QESButton } from "@app/shared/qes-standards/Button";
import { resolvePrimaryColor, resolveTierForScore } from "@app/shared/confidence-tier/confidenceTierLookup";
import { useConfidenceTierDefinitions } from "@app/shared/confidence-tier/useConfidenceTierDefinitions";
import { valueOrDashes } from "@app/shared/utility/valueOrDashes";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail";

interface QuestProviderDataInsightsProps {
  record: ServicingProviderDetail;
}

type PgrSignalScoreKey =
  | "scoreLocationConfidence"
  | "scoreSpecialtyEvidence"
  | "scoreIdentityIntegrity"
  | "scoreActivitySignal"
  | "scoreSourceConvergence";

interface PgrSignalRow {
  key: PgrSignalScoreKey;
  label: string;
  testId: string;
}

// Ordered by signal weight, not alphabetically.
const PGR_SIGNAL_ROWS: PgrSignalRow[] = [
  { key: "scoreLocationConfidence", label: "Location Confidence", testId: "pgr-signal-location-confidence" },
  { key: "scoreSpecialtyEvidence", label: "Specialty Evidence", testId: "pgr-signal-specialty-evidence" },
  { key: "scoreIdentityIntegrity", label: "Identity Integrity", testId: "pgr-signal-identity-integrity" },
  { key: "scoreActivitySignal", label: "Activity Signal", testId: "pgr-signal-activity-signal" },
  { key: "scoreSourceConvergence", label: "Source Convergence", testId: "pgr-signal-source-convergence" },
];

const CONFIDENCE_TIER_DESCRIPTION =
  "Confidence Level combines the signals below into a single weighted score.";

const NO_CONFIDENCE_DATA_MESSAGE = "There are no records for this selection.";

const WHAT_IS_THIS_PENDO_ID = "pgr-access-point-confidence-level-what-is-this-link";

// Truncated, not rounded, for display.
const formatScore = (score: number | null | undefined): React.ReactNode =>
  score == null ? valueOrDashes(null) : String(Math.trunc(score));

export const QuestProviderDataInsights: React.FC<QuestProviderDataInsightsProps> = ({ record }): JSX.Element => {
  const { definitions } = useConfidenceTierDefinitions();

  const hasConfidenceData = record.pgrCompositeScore != null
    || record.pgrConfidenceTier != null
    || PGR_SIGNAL_ROWS.some((signal) => record[signal.key] != null);

  if (!hasConfidenceData) {
    return (
      <div className="qes-pgr-insights">
        <div className="text-muted" data-testid="pgr-confidence-no-data">{NO_CONFIDENCE_DATA_MESSAGE}</div>
      </div>
    );
  }

  return (
    <div className="qes-pgr-insights">
      <div className="qes-pgr-insights__summary">
        <span className="qes-pgr-insights__tier-label">Confidence Level</span>
        {record.pgrConfidenceTier != null
          ? (
            <ConfidenceTierBadge
              confidenceTier={record.pgrConfidenceTier}
              testId="pgr-confidence-tier-badge"
            />
          )
          : <span data-testid="pgr-confidence-tier-empty">{valueOrDashes(null)}</span>}
        {record.pgrCompositeScore == null
          ? <span className="qes-pgr-insights__score" data-testid="pgr-confidence-index-score">{valueOrDashes(null)}</span>
          : (
            <span className="qes-pgr-insights__score" data-testid="pgr-confidence-index-score">
              <span className="qes-pgr-insights__score-value" data-testid="pgr-confidence-index-score-value">
                {formatScore(record.pgrCompositeScore)}
              </span>
              <span className="qes-pgr-insights__score-max">/100</span>
            </span>
          )}
      </div>
      {/* No click handler: the Pendo guide supplies the behavior. */}
      <QESButton
        className={cx("qes-pgr-insights__what-is-this", WHAT_IS_THIS_PENDO_ID)}
        id={WHAT_IS_THIS_PENDO_ID}
        qesTestId="pgr-confidence-what-is-this"
        qesType="link"
      >
        What is this?
      </QESButton>
      <div className="qes-pgr-insights__description" data-testid="pgr-confidence-tier-description">
        {CONFIDENCE_TIER_DESCRIPTION}
      </div>
      {PGR_SIGNAL_ROWS.map((signal) => {
        const score = record[signal.key];

        return (
          <div className="qes-pgr-insights__signal" key={signal.key}>
            <div className="qes-pgr-insights__signal-header">
              <span className="qes-pgr-insights__signal-label">{signal.label}</span>
              <span className="qes-pgr-insights__signal-score" data-testid={signal.testId}>
                {formatScore(score)}
              </span>
            </div>
            <div
              className={cx("qes-pgr-insights__track", { "qes-pgr-insights__track--no-data": score == null })}
              data-testid={`${signal.testId}-track`}
            >
              {score != null && (
                // Colored by the bar's own score, not the provider's tier.
                <div
                  className="qes-pgr-insights__fill"
                  data-testid={`${signal.testId}-fill`}
                  style={{
                    backgroundColor: resolvePrimaryColor(resolveTierForScore(definitions, score)?.primaryColor),
                    width: `${score}%`,
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
